import type { LightingConfig } from './types';
import type { EnvironmentConfig } from '../environment/types';
import type { ColorGradeConfig } from '../core/colorGrade';

export interface LightingPreset {
  id: string;
  label: string;
  lighting: LightingConfig;
  environment: EnvironmentConfig;
  colorGrade: ColorGradeConfig;
}

/**
 * A preset pairs a LightingConfig (sun angle/color/shadow tuning) with a
 * matching EnvironmentConfig (sky turbidity/rayleigh + fog) — the two
 * always need to agree (a low golden-hour sun with a noon-blue sky and no
 * haze would look wrong), so bundling them here means switching "time of
 * day" is one config swap (`course.lighting`/`course.environment`, or a
 * future in-game time control) rather than two configs that can drift out
 * of sync if tuned independently.
 *
 * Applying a preset is just `engine.lighting.applyConfig(preset.lighting)`
 * + `await engine.environment.applyConfig(preset.environment)` — the same
 * two calls Course.load() already makes, just with a named config instead
 * of an ad-hoc one.
 */
export const LIGHTING_PRESETS: Record<string, LightingPreset> = {
  morning: {
    id: 'morning',
    label: 'Morning',
    lighting: {
      sunAzimuthDeg: 100,
      sunElevationDeg: 22,
      sunColor: 0xfff0dd,
      sunIntensity: 2.6,
      hemisphereSkyColor: 0xcfe3ff,
      hemisphereGroundColor: 0x33431f,
      hemisphereIntensity: 0.55,
      shadowFrustumSize: 80,
    },
    environment: {
      sky: {
        kind: 'sky',
        sunAzimuthDeg: 100,
        sunElevationDeg: 22,
        turbidity: 4,
        rayleigh: 2.2,
        mieCoefficient: 0.004,
      },
      fog: { color: 0xd7e6ff, near: 140, far: 520 },
    },
    colorGrade: { exposure: 1.0, contrast: 1.02, saturation: 1.0 },
  },

  noon: {
    id: 'noon',
    label: 'Noon',
    lighting: {
      sunAzimuthDeg: 180,
      sunElevationDeg: 75,
      sunColor: 0xffffff,
      sunIntensity: 3.6,
      hemisphereSkyColor: 0xbfd7ff,
      hemisphereGroundColor: 0x2b3a1f,
      hemisphereIntensity: 0.7,
      shadowFrustumSize: 80,
    },
    environment: {
      sky: {
        kind: 'sky',
        sunAzimuthDeg: 180,
        sunElevationDeg: 75,
        turbidity: 2,
        rayleigh: 1,
        mieCoefficient: 0.003,
      },
      fog: { color: 0xdcebff, near: 260, far: 760 },
    },
    colorGrade: { exposure: 1.0, contrast: 1.08, saturation: 1.05 },
  },

  afternoon: {
    id: 'afternoon',
    label: 'Afternoon',
    lighting: {
      sunAzimuthDeg: 235,
      sunElevationDeg: 34,
      sunColor: 0xffedc8,
      sunIntensity: 2.9,
      hemisphereSkyColor: 0xf0ead8,
      hemisphereGroundColor: 0x536b34,
      hemisphereIntensity: 0.62,
      shadowFrustumSize: 90,
    },
    environment: {
      sky: {
        kind: 'sky',
        sunAzimuthDeg: 235,
        sunElevationDeg: 34,
        turbidity: 3.8,
        rayleigh: 1.25,
        mieCoefficient: 0.0045,
        mieDirectionalG: 0.78,
      },
      fog: { color: 0xe8ead8, near: 420, far: 980 },
    },
    colorGrade: { exposure: 1.02, contrast: 1.06, saturation: 1.08 },
  },

  goldenHour: {
    id: 'goldenHour',
    label: 'Golden Hour',
    lighting: {
      sunAzimuthDeg: 250,
      sunElevationDeg: 12,
      sunColor: 0xffc98a,
      sunIntensity: 3.0,
      hemisphereSkyColor: 0xffd9a8,
      hemisphereGroundColor: 0x3a2e1a,
      hemisphereIntensity: 0.5,
      shadowFrustumSize: 80,
    },
    environment: {
      sky: {
        kind: 'sky',
        sunAzimuthDeg: 250,
        sunElevationDeg: 12,
        turbidity: 6,
        rayleigh: 2.6,
        mieCoefficient: 0.01,
        mieDirectionalG: 0.85,
      },
      fog: { color: 0xf3c98f, near: 160, far: 600 },
    },
    colorGrade: { exposure: 1.08, contrast: 1.1, saturation: 1.18 },
  },

  cloudy: {
    id: 'cloudy',
    label: 'Cloudy',
    lighting: {
      sunAzimuthDeg: 210,
      sunElevationDeg: 55,
      sunColor: 0xe4e8ee,
      sunIntensity: 1.4,
      hemisphereSkyColor: 0xb9bfc7,
      hemisphereGroundColor: 0x2b3a1f,
      hemisphereIntensity: 0.85,
      shadowFrustumSize: 80,
    },
    environment: {
      // Flat overcast light doesn't read well from a clear-sky Preetham/Nishita
      // model (no sun disc, low contrast) — procedural IBL + a heavier fog
      // reads as overcast far more convincingly than forcing the Sky shader here.
      sky: { kind: 'procedural' },
      fog: { color: 0xaab0b8, near: 90, far: 380 },
    },
    colorGrade: { exposure: 0.95, contrast: 0.92, saturation: 0.82 },
  },
};
